/**
 * GTM Data Layer utility for CreateAnAPI Portal.
 *
 * Pushes events to window.dataLayer so GTM container GTM-K9Z2SCZ8
 * can pick them up via Custom Event triggers.
 *
 */

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * SHA-256 hash a string and return a hex digest.
 * Uses the native Web Crypto API (available in all modern browsers).
 * Returns a Promise<string>.
 */
async function sha256(value) {
    const msgBuffer = new TextEncoder().encode(value);
    const hashBuffer = await window.crypto.subtle.digest("SHA-256", msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

function getDataLayer() {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Push a virtual page view event (SPA route change).
 * Replaces: trackPageView(path)
 *
 * @param {string} path - The current route path (location.pathname)
 */
export const pushPageView = (path) => {
    getDataLayer().push({
        event: "virtual_page_view",
        page_path: path,
        page_title: document.title,
    });
};

/**
 * Push a user identified event (login / account selection).
 * Replaces: identifyUser(userId, accountId, accountName)
 *
 * User ID and Account ID are SHA-256 hashed before being pushed
 * to the data layer for privacy.
 *
 * @param {string} userId    - Raw user ID
 * @param {string} accountId - Raw account ID
 * @param {string} accountName - Account display name (not hashed)
 * @param {string} [userRole] - User role within the account (e.g. "admin")
 */
export const pushUserIdentified = (userId, accountId, accountName, userRole) => {
    Promise.all([sha256(userId), sha256(accountId)]).then(
        ([userIdHashed, accountIdHashed]) => {
            getDataLayer().push({
                event: "user_identified",
                user_id_hashed: userIdHashed,
                account_id_hashed: accountIdHashed,
                user_role: userRole || "",
                caa_user_id: userId,
                account_id: accountId,
                account_name: accountName || "",
            });
        }
    );
};

/**
 * Push a generic custom event.
 * Use for future events like feature_used, subscription_started, etc.
 *
 * @param {string} eventName - The custom event name
 * @param {Object} [data]    - Additional key-value pairs to include
 */
export const pushEvent = (eventName, data = {}) => {
    getDataLayer().push({
        event: eventName,
        ...data,
    });
};
